import { Input } from "@brg/input";
import { DropdownListItem, FlexBox } from "@brg/ui";
import { FC, useEffect, useMemo, useReducer, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Lang } from "@brg/api";
import { ArrowDownSVG } from "shared/assets/16/ArrowDown";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { useTranslation } from "react-i18next";
import { ITeamButton } from "../types";
import setTextByLang from "shared/helpers/setTextByLang";

export enum SpecialButtonsType {
    connect = 'connect',
    link = 'link',
    form = 'form',
}

export const isSpecialButton = (type: SpecialButtonsType) => {
    return [SpecialButtonsType.connect]?.includes(type)
}

const Wrapper = styled(FlexBox)`
    position: relative;
    width: 100%;

    input {
        padding-right: 34px;
    }
`;

const DropdownBlock = styled.div<{ offset: number }>`
    visibility: visible;
    position: absolute;
    left: 0;
    top: ${({ offset }) => offset ? `${offset + 10}px` : '50px'};
    background-color: #909090;
    width: 100%;
    z-index: 10;

    ${DropdownListItem} {
        display: flex;
        flex-flow: row;
        justify-content: space-between;
        font-size: 13px;
        line-height: 16px;
    }
`;

const ArrowButton = styled.div<{ open: boolean }>`
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    cursor: pointer;
    transition: transform 0.2s;

    ${({ open }) => open && css`
        svg {
            transform: rotate(-180deg);
            transition: transform 0.2s;

            path {
                fill: #151514 !important;
            }
            g {
                opacity: 1;
            }
        }    
        `};
`;

export const SpecialDropdownButtons: FC<{ onChange: (button: ITeamButton, index: number) => void, button: any, currentLang: string, index: number, actualForms?: any }> = ({ onChange, button, currentLang, index, actualForms }) => {
    const [currentType, setCurrentType] = useState<SpecialButtonsType>()
    const [visible, toggleVisible] = useReducer((v) => !v, false);
    const inputRef = useRef<HTMLDivElement>(null);
    const {t} = useTranslation();

    const inputHeight = useMemo(() => (inputRef?.current?.clientHeight) , [inputRef?.current]);

    const buttonTypes = useMemo(() => {
        const allTypes = Object.values(SpecialButtonsType);
        return actualForms?.length ? allTypes : allTypes.filter(type => type !== SpecialButtonsType.form);
    }, [actualForms]);

    const handleButtonTypeChange = (type: SpecialButtonsType ) => {
        setCurrentType(type);
        toggleVisible();
        
        const baseUpdate = {
            ...button,
            actionType: type,
            text: setTextByLang(
                createDefaultLangObject(
                    t(`teams.adminPage.dropdownButtons.specialType.${type}`)
                ),
                currentLang,
                t(`teams.adminPage.dropdownButtons.specialType.${type}`)
            ),
            textMobile: setTextByLang(
                createDefaultLangObject(
                    t(`teams.adminPage.dropdownButtons.specialType.${type}`)
                ),
                currentLang,
                t(`teams.adminPage.dropdownButtons.specialType.${type}`)
            )
        };
      
        if (type === SpecialButtonsType.link) {
            onChange({
                ...baseUpdate,
                href: setTextByLang(button.href, currentLang, Lang(button.href, currentLang)),
            }, index);
        } else if (type === SpecialButtonsType.form && actualForms?.[0]?.formAddress) {
              console.log('worked', setTextByLang(button.href, currentLang, actualForms[0].formAddress));
            onChange({
                ...baseUpdate,
                href: setTextByLang(button.href, currentLang, actualForms[0].formAddress),
            }, index);
        } else {
            onChange(baseUpdate, index);
        }
    }

    useEffect(() => {
        if (!currentType) {
            //@ts-ignore
            setCurrentType(SpecialButtonsType[button?.actionType] || SpecialButtonsType.link)
        }
    }, [button, currentType])

    const currentInput = useMemo(() => {
        if (currentType === SpecialButtonsType.link) {
            return (
                <Input.Text
                    value={!!button?.isRequired ? '' : Lang(button.href, currentLang)}
                    disabled={!!button?.isRequired}
                    onChange={(value) => onChange(
                        {
                            ...button, 
                            actionType: SpecialButtonsType.link, 
                            //@ts-ignore
                            href: setTextByLang(button.href, currentLang, value.target.value) 
                        }
                        , index
                    )}
                />
            )
        } else if (currentType === SpecialButtonsType.form) {
            return (
                <Input.Text
                    value={actualForms?.[0]?.formAddress || t(`teams.adminPage.dropdownButtons.specialType.${currentType}`) as string}
                    disabled={true}
                />
            )
        } else {
            return (
                <Input.Text
                    value={t(`teams.adminPage.dropdownButtons.specialType.${currentType}`) as string}
                    disabled={true}
                />
            )
        }
    }, [currentType, button, onChange, index, currentLang, t, actualForms])
    
    return (
        <Wrapper>
            <FlexBox ref={inputRef} widthMobile={'100%'} width={'100%'}>
               {currentInput}
            </FlexBox>
            <ArrowButton onClick={toggleVisible} open={visible}>
                <ArrowDownSVG clr={'#151514'} />
            </ArrowButton>
            {visible && (
                <DropdownBlock offset={inputHeight ?? 0}>
                    {buttonTypes?.map((type) => {
                        return (
                            <DropdownListItem
                                onClick={() => handleButtonTypeChange(type)} 
                                isActive={type === currentType} 
                                key={`type_id_${type}`}
                            >
                                {t(`teams.adminPage.dropdownButtons.specialType.${type}`)}
                                {type === currentType && (
                                    <svg className={'approve-icon'} width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                                            fill={'#151514'}/>
                                    </svg>
                                )}
                            </DropdownListItem> 
                        )
                    })}
                </DropdownBlock>
            )}
        </Wrapper>
    )
}import { Input } from "@brg/input";
import { DropdownListItem, FlexBox } from "@brg/ui";
import { FC, useEffect, useMemo, useReducer, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Lang } from "@brg/api";
import { ArrowDownSVG } from "shared/assets/16/ArrowDown";
import { createDefaultLangObject } from "shared/components/__ADDONS__/__SAFEAPI__/Defaults";
import { useTranslation } from "react-i18next";
import { ITeamButton } from "../types";
import setTextByLang from "shared/helpers/setTextByLang";

export enum SpecialButtonsType {
    connect = 'connect',
    link = 'link',
    form = 'form',
}

export const isSpecialButton = (type: SpecialButtonsType) => {
    return [SpecialButtonsType.connect]?.includes(type)
}

const Wrapper = styled(FlexBox)`
    position: relative;
    width: 100%;

    input {
        padding-right: 34px;
    }
`;

const DropdownBlock = styled.div<{ offset: number }>`
    visibility: visible;
    position: absolute;
    left: 0;
    top: ${({ offset }) => offset ? `${offset + 10}px` : '50px'};
    background-color: #909090;
    width: 100%;
    z-index: 10;

    ${DropdownListItem} {
        display: flex;
        flex-flow: row;
        justify-content: space-between;
        font-size: 13px;
        line-height: 16px;
    }
`;

const ArrowButton = styled.div<{ open: boolean }>`
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    cursor: pointer;
    transition: transform 0.2s;

    ${({ open }) => open && css`
        svg {
            transform: rotate(-180deg);
            transition: transform 0.2s;

            path {
                fill: #151514 !important;
            }
            g {
                opacity: 1;
            }
        }    
        `};
`;

export const SpecialDropdownButtons: FC<{ onChange: (button: ITeamButton, index: number) => void, button: any, currentLang: string, index: number, actualForms?: any }> = ({ onChange, button, currentLang, index, actualForms }) => {
    const [currentType, setCurrentType] = useState<SpecialButtonsType>()
    const [visible, toggleVisible] = useReducer((v) => !v, false);
    const inputRef = useRef<HTMLDivElement>(null);
    const {t} = useTranslation();

    const inputHeight = useMemo(() => (inputRef?.current?.clientHeight) , [inputRef?.current]);

    const buttonTypes = useMemo(() => {
        const allTypes = Object.values(SpecialButtonsType);
        return actualForms?.length ? allTypes : allTypes.filter(type => type !== SpecialButtonsType.form);
    }, [actualForms]);

    const handleButtonTypeChange = (type: SpecialButtonsType ) => {
        setCurrentType(type);
        toggleVisible();
        
        const baseUpdate = {
            ...button,
            actionType: type,
            text: setTextByLang(
                createDefaultLangObject(
                    t(`teams.adminPage.dropdownButtons.specialType.${type}`)
                ),
                currentLang,
                t(`teams.adminPage.dropdownButtons.specialType.${type}`)
            ),
            textMobile: setTextByLang(
                createDefaultLangObject(
                    t(`teams.adminPage.dropdownButtons.specialType.${type}`)
                ),
                currentLang,
                t(`teams.adminPage.dropdownButtons.specialType.${type}`)
            )
        };
      
        if (type === SpecialButtonsType.link) {
            onChange({
                ...baseUpdate,
                href: setTextByLang(button.href, currentLang, Lang(button.href, currentLang)),
            }, index);
        } else if (type === SpecialButtonsType.form && actualForms?.[0]?.formAddress) {
              console.log('worked', setTextByLang(button.href, currentLang, actualForms[0].formAddress));
            onChange({
                ...baseUpdate,
                href: setTextByLang(button.href, currentLang, actualForms[0].formAddress),
            }, index);
        } else {
            onChange(baseUpdate, index);
        }
    }

    useEffect(() => {
        if (!currentType) {
            //@ts-ignore
            setCurrentType(SpecialButtonsType[button?.actionType] || SpecialButtonsType.link)
        }
    }, [button, currentType])

    const currentInput = useMemo(() => {
        if (currentType === SpecialButtonsType.link) {
            return (
                <Input.Text
                    value={!!button?.isRequired ? '' : Lang(button.href, currentLang)}
                    disabled={!!button?.isRequired}
                    onChange={(value) => onChange(
                        {
                            ...button, 
                            actionType: SpecialButtonsType.link, 
                            //@ts-ignore
                            href: setTextByLang(button.href, currentLang, value.target.value) 
                        }
                        , index
                    )}
                />
            )
        } else if (currentType === SpecialButtonsType.form) {
            return (
                <Input.Text
                    value={actualForms?.[0]?.formAddress || t(`teams.adminPage.dropdownButtons.specialType.${currentType}`) as string}
                    disabled={true}
                />
            )
        } else {
            return (
                <Input.Text
                    value={t(`teams.adminPage.dropdownButtons.specialType.${currentType}`) as string}
                    disabled={true}
                />
            )
        }
    }, [currentType, button, onChange, index, currentLang, t, actualForms])
    
    return (
        <Wrapper>
            <FlexBox ref={inputRef} widthMobile={'100%'} width={'100%'}>
               {currentInput}
            </FlexBox>
            <ArrowButton onClick={toggleVisible} open={visible}>
                <ArrowDownSVG clr={'#151514'} />
            </ArrowButton>
            {visible && (
                <DropdownBlock offset={inputHeight ?? 0}>
                    {buttonTypes?.map((type) => {
                        return (
                            <DropdownListItem
                                onClick={() => handleButtonTypeChange(type)} 
                                isActive={type === currentType} 
                                key={`type_id_${type}`}
                            >
                                {t(`teams.adminPage.dropdownButtons.specialType.${type}`)}
                                {type === currentType && (
                                    <svg className={'approve-icon'} width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                                            fill={'#151514'}/>
                                    </svg>
                                )}
                            </DropdownListItem> 
                        )
                    })}
                </DropdownBlock>
            )}
        </Wrapper>
    )
}